import useGenerateFlowService from "@/service-hooks/use-generate-flow-service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useParams } from "react-router-dom";
import useGetDocumentService from "@/service-hooks/use-get-document-service";
import { toast } from "sonner";
import { compileDiagram } from "@/lib/diagram-components.lib";
import { useDiagram } from "@/context/diagram-context";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import { useUI } from "@/context/ui-context";
import { useLibrary } from "@/context/library-context";
import { TOAST_DURATION } from "@/lib/constants";
import { useEffect, useCallback } from "react";

interface AttachmentMenuProps {
  trigger: React.ReactNode;
  dropdownControls: {
    isDropdownOpen: boolean;
    setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  handleUploadBoxOpen: () => void;
  hideGenerateOptions?: boolean;
}
export default function AttachmentLayout(props: AttachmentMenuProps) {
  const { id: uuid = "" } = useParams();
  const diagram = useDiagram();
  const { showAttachmentPoints } = useUI();
  const { handleMissingDependencies } = useLibrary();

  const {
    dropdownControls,
    hideGenerateOptions,
  } = props;
  const { isDropdownOpen, setIsDropdownOpen } = dropdownControls;
  const { data } = useGetDocumentService(uuid, true);
  const { mutate, abort } = useGenerateFlowService(async (res, error) => {
    // Always stop loading state
    diagram.setIsDiagramLoading(false);

    // Handle error cases
    if (error) {
      // Don't show error toast for user-cancelled requests
      if (error.name !== "AbortError") {
        toast.error(error.message || "Failed to generate diagram", {
          duration: TOAST_DURATION,
        });
      }
      return;
    }

    // Handle success case
    if (res?.metadata?.result && res?.metadata?.result?.length > 0) {
      const diagramComponents = res.metadata.result;
      await handleMissingDependencies(diagramComponents);
      const { svgContent, processedComponents } = compileDiagram(
        diagramComponents,
        diagram.canvasSize,
        shapesLibraryManager.getAllShapes(),
        showAttachmentPoints
      );
      diagram.setDiagramComponents(processedComponents);
      diagram.setComposedSVG(svgContent);
      diagram.selectShape(null);
    } else {
      toast.error("No diagram components found", {
        duration: TOAST_DURATION,
      });
    }
  });
  const imageFiles =
    data?.data?.filter((doc) => doc.metadata.fileType === "image") ?? [];

  // Memoized handler to abort diagram generation
  const handleAbortDiagramGeneration = useCallback(() => {
    abort();
    diagram.setIsDiagramLoading(false);
    toast.info("Diagram generation cancelled", {
      duration: TOAST_DURATION,
    });
  }, [abort, diagram]);

  // Listen for abort diagram generation event
  useEffect(() => {
    window.addEventListener("abortDiagramGeneration", handleAbortDiagramGeneration);

    return () => {
      window.removeEventListener("abortDiagramGeneration", handleAbortDiagramGeneration);
    };
  }, [handleAbortDiagramGeneration]);

  return (
    <DropdownMenu
      open={isDropdownOpen}
      onOpenChange={(e) => {
        setIsDropdownOpen(e);
      }}
    >
      <DropdownMenuTrigger asChild>{props.trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-surface">
        <DropdownMenuGroup>
          {!hideGenerateOptions && (
            <>
              <DropdownMenuItem
                onSelect={() => {
                  diagram.setIsDiagramLoading(true);
                  mutate({ key: "blueprint", uuid });
                }}
                className="text-text-primary"
              >
                Generate Diagram from Architecture Ontology
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-text-primary">
                  Generate Diagram
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="bg-surface">
                    {imageFiles?.length > 0 ? (
                      imageFiles.map((doc) => (
                        <DropdownMenuItem
                          key={doc._id}
                          onClick={() => {
                            diagram.setIsDiagramLoading(true);
                            mutate({
                              uuid,
                              key: "diagram",
                              documentId: doc._id,
                            });
                          }}
                          className="text-text-primary"
                        >
                          {doc.metadata.fileName}
                        </DropdownMenuItem>
                      ))
                    ) : (
                      <DropdownMenuItem className="text-text-primary">
                        Image not found.
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
